import React from 'react';
import PropTypes from 'prop-types';
import {
  Anchor,
  Badge,
  Divider,
  NumberFormatter,
  Skeleton
} from '@mantine/core';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppFlexbox from '../../common/AppFlexbox';
import { formatUtcDate } from '../../../helpers/format';
import { getRegFormSubmissionStatusInfo } from '../../../helpers/registrationHelper';
import ColoredAvatar from '../../common/ColoredAvatar';
import OrderProductsCostInfo from './OrderProductsCostInfo';
import CheckoutLineItemAddonsDisplay from '../checkout/CheckoutLineItemAddonsDisplay';

const RegCartProductPreview = ({
  product,
  loading,
  showPrice,
  hideStatus,
  hideInputAddons,
  warningMessage,
  showCancelWarnings,
  showCustomerInfo,
  urlBase,
  productNavigationOverrideUrl,
  disableNavigation
}) => {
  const statusInfo = getRegFormSubmissionStatusInfo(product?.regFormSubmission);
  const toUrl =
    productNavigationOverrideUrl ||
    (product?.to ? `${urlBase}${product.to}` : null);
  const showNavigation = !!toUrl && !disableNavigation;

  return !loading && product ? (
    <AppFlexbox style={{ gap: 8 }}>
      <ColoredAvatar
        name={`${product.regFormSubmission.regPerson.firstName} ${product.regFormSubmission.regPerson.lastName}`}
        size={42}
        uniqueId={product.regFormSubmission.user.email}
      />
      <AppStack style={{ gap: 0 }}>
        <AppStack style={{ gap: 0 }}>
          <AppFlexbox
            component={showNavigation ? Link : null}
            style={{ color: '#000', textDecoration: 'none' }}
            to={showNavigation ? toUrl : null}
          >
            <AppStack style={{ gap: 0 }}>
              <AppFlexbox style={{ gap: 8 }}>
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                  {
                    product.regFormSubmission.regAssociationDivisionForm.regForm
                      .name
                  }
                </AppText>
                {!hideStatus && (
                  <Badge
                    color={statusInfo.color}
                    variant={statusInfo.variant || 'filled'}
                  >
                    {statusInfo.label}
                  </Badge>
                )}
              </AppFlexbox>

              <AppText
                style={{
                  fontSize: 14,
                  color: '#666',
                  textDecoration: product.regFormSubmission
                    .regAssociationDivisionOverride
                    ? 'line-through'
                    : 'unset'
                }}
              >
                {
                  product.regFormSubmission.regAssociationDivisionForm
                    .regAssociationDivision.name
                }{' '}
                &middot;{' '}
                {
                  product.regFormSubmission.regAssociationDivisionForm
                    .regAssociationDivision.regAssociationSeason.name
                }
              </AppText>

              {product.regFormSubmission.regAssociationDivisionOverride && (
                <AppText style={{ fontSize: 14, color: '#666' }}>
                  {
                    product.regFormSubmission.regAssociationDivisionOverride
                      .name
                  }{' '}
                  &middot;{' '}
                  {
                    product.regFormSubmission.regAssociationDivisionOverride
                      .regAssociationSeason.name
                  }
                </AppText>
              )}

              <AppText style={{ fontSize: 14, color: '#666' }}>
                {product.regFormSubmission.regPerson.firstName}{' '}
                {product.regFormSubmission.regPerson.lastName}
              </AppText>

              <CheckoutLineItemAddonsDisplay
                addOns={product.lineItem.addOns.filter(
                  (f) => !hideInputAddons || !f.isInputAddon
                )}
              />
            </AppStack>
          </AppFlexbox>

          {showPrice && (
            <>
              <NumberFormatter
                decimalScale={2}
                displayType="text"
                fixedDecimalScale
                prefix="$"
                style={{ fontSize: 14 }}
                thousandSeparator
                value={product.lineItem.subtotalInCents / 100}
              />

              {product.tax && product.tax.length > 0 && (
                <AppFlexbox>
                  <OrderProductsCostInfo
                    justifyContent="start"
                    tax={product.tax.map((t) => ({
                      name: t.name,
                      amount: t.taxInCents,
                      rate: t.taxRate
                    }))}
                  />
                </AppFlexbox>
              )}
            </>
          )}

          {showCustomerInfo && (
            <AppStack style={{ gap: 0 }}>
              <Divider style={{ margin: '4px 0px' }} />
              {product.ecomCustomer.user && (
                <AppText style={{ fontSize: 14 }}>
                  {product.ecomCustomer.user.name}
                </AppText>
              )}
              <Anchor
                c="dodgerblue"
                component="a"
                href={`mailto:${product.ecomCustomer.email}`}
                style={{ fontSize: 14 }}
              >
                {product.ecomCustomer.email}
              </Anchor>
              <AppText
                style={{
                  fontSize: 12,
                  color: '#666'
                }}
              >
                Purchased{' '}
                {dayjs(
                  formatUtcDate(product.ecomOrderProduct.createdAt)
                ).format('MMMM D, YYYY h:mm A')}
              </AppText>
            </AppStack>
          )}
        </AppStack>
        {warningMessage && (
          <AppText
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: '#C40000',
              marginTop: 5
            }}
          >
            {warningMessage}
          </AppText>
        )}
        {showCancelWarnings && product.isBulkShipment && (
          <AppText
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: '#C40000',
              marginTop: 5
            }}
          >
            Cancelling this product may impact the fulfillment of the group
            order.
          </AppText>
        )}
      </AppStack>
    </AppFlexbox>
  ) : (
    <AppFlexbox style={{ width: '100%', flex: 1, gap: 8 }}>
      <AppStack
        style={{
          width: 50,
          height: 50,
          overflow: 'hidden',
          borderRadius: 8,
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Skeleton height="100%" width="100%" />
      </AppStack>
      <AppStack style={{ width: '100%', gap: 5 }}>
        <Skeleton height={18} width="40%" />

        <Skeleton height={16} width="30%" />
        <Skeleton height={16} width="30%" />
      </AppStack>
    </AppFlexbox>
  );
};

RegCartProductPreview.propTypes = {
  disableNavigation: PropTypes.bool,
  hideInputAddons: PropTypes.bool,
  hideStatus: PropTypes.bool,
  loading: PropTypes.bool,
  product: PropTypes.object,
  productNavigationOverrideUrl: PropTypes.string,
  showCancelWarnings: PropTypes.bool,
  showCustomerInfo: PropTypes.bool,
  showPrice: PropTypes.bool,
  urlBase: PropTypes.string,
  warningMessage: PropTypes.string
};

export default RegCartProductPreview;
